import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState<"checking" | "paid" | "failed">("checking");
  const [planName, setPlanName] = useState<string | null>(null);
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("failed");
      return;
    }
    let timer: any;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setPlanName(data.plan_name);
        if (data.payment_status === "paid") {
          setState("paid");
          await refresh();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch {
        setState("failed");
        return;
      }
      attempts.current += 1;
      if (attempts.current > 12) {
        setState("failed");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-6" data-testid="payment-success-screen">
      <div className="max-w-md w-full card-obsidian p-8 text-center">
        <div className="flex items-center justify-center mb-6"><BrandLogo /></div>
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-gold animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-serif font-medium mb-2">Confirmando tu pago…</h1>
            <p className="text-sm text-slate-500">Esto solo tomará unos segundos.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-serif font-medium mb-2">¡Pago recibido!</h1>
            <p className="text-sm text-slate-500">
              Contrataste el plan {planName ? <b>{planName}</b> : ""}. En unos minutos podrás entrar a la app
              Armony Contigo con tu mismo <b>correo y contraseña</b> de Armony.
            </p>
            <Button data-testid="success-go-plans" onClick={() => navigate("/portal/planes")} className="mt-6 gold-gradient-bg text-white font-semibold rounded-full w-full">
              Ver mi plan
            </Button>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="w-14 h-14 text-red-600 mx-auto mb-4" />
            <h1 className="text-2xl font-serif font-medium mb-2">No pudimos confirmar el pago</h1>
            <p className="text-sm text-slate-500">Si el cargo se realizó, tu plan se activará en breve. Puedes volver a intentarlo.</p>
            <Button data-testid="failed-go-plans" onClick={() => navigate("/portal/planes")} className="mt-6 bg-navy text-white font-semibold rounded-full w-full">
              Volver a planes
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
